if (!customElements.get('product-modal')) {
  customElements.define('product-modal', class ProductModal extends HTMLElement {
    constructor() {
      super();
      this.swipeState = {
        active: false,
        pointerId: null,
        startX: 0,
        startY: 0,
        isHorizontal: null,
        activeMedia: null,
        prevMedia: null,
        nextMedia: null,
        viewportWidth: 0
      };
      this.zoomState = {
        active: false,
        panning: false,
        scale: 1,
        tx: 0,
        ty: 0,
        startDistance: 0,
        startScale: 1,
        startTx: 0,
        startTy: 0,
        startMidX: 0,
        startMidY: 0,
        lastX: 0,
        lastY: 0,
        viewportWidth: 0,
        viewportHeight: 0
      };
      this.touchSwipeState = {
        active: false,
        startX: 0,
        startY: 0,
        isHorizontal: null,
        activeMedia: null,
        prevMedia: null,
        nextMedia: null,
        viewportWidth: 0
      };

      this.addEventListener('click', (event) => {
        if (event.target === this || event.target.closest('.product-media-modal__button--close')) {
          this.hide();
        }
        
        const prevButton = event.target.closest('.product-media-modal__button--prev');
        const nextButton = event.target.closest('.product-media-modal__button--next');
        
        if (prevButton || nextButton) {
          event.preventDefault();
          event.stopPropagation();
          this.slide(prevButton ? 'prev' : 'next');
        }
      });

      window.addEventListener('keydown', (event) => {
        if (!this.hasAttribute('open')) return;
        if (event.code === 'ArrowLeft') this.slide('prev');
        if (event.code === 'ArrowRight') this.slide('next');
        if (event.code === 'Escape') this.hide();
      });

      this.addEventListener('pointerdown', (event) => {
        if (!this.hasAttribute('open')) return;
        if (event.pointerType === 'touch') return;
        if (event.pointerType === 'mouse' && event.buttons !== 1) return;
        if (event.target.closest('.product-media-modal__button')) return;

        const content = event.target.closest('.product-media-modal__content');
        if (!content) return;

        const activeMedia = this.querySelector('.product-media-modal__content > .active');
        if (!activeMedia) return;

        const mediaElements = Array.from(this.querySelectorAll('.product-media-modal__content > [data-media-id]'));
        if (mediaElements.length <= 1) return;

        const currentIndex = mediaElements.indexOf(activeMedia);
        const prevMedia = mediaElements[currentIndex - 1] || mediaElements[mediaElements.length - 1];
        const nextMedia = mediaElements[currentIndex + 1] || mediaElements[0];

        this.swipeState.active = true;
        this.swipeState.pointerId = event.pointerId;
        this.swipeState.startX = event.clientX;
        this.swipeState.startY = event.clientY;
        this.swipeState.isHorizontal = null;
        this.swipeState.activeMedia = activeMedia;
        this.swipeState.prevMedia = prevMedia;
        this.swipeState.nextMedia = nextMedia;
        this.swipeState.viewportWidth = content.getBoundingClientRect().width || window.innerWidth;

        this.classList.add('is-dragging');
      });

      this.addEventListener(
        'touchstart',
        (event) => {
          if (!this.hasAttribute('open')) return;
          if (event.target.closest('.product-media-modal__button')) return;
          const content = event.target.closest('.product-media-modal__content');
          if (!content) return;

          const activeMedia = this.querySelector('.product-media-modal__content > .active');
          if (!activeMedia) return;

          if (event.touches.length === 2 && activeMedia.tagName === 'IMG') {
            event.preventDefault();
            this.startPinch(event, content, activeMedia);
            return;
          }

          if (event.touches.length === 1) {
            if (this.zoomState.scale > 1 && activeMedia.tagName === 'IMG') {
              event.preventDefault();
              this.startPan(event, content, activeMedia);
              return;
            }
            this.startTouchSwipe(event, content, activeMedia);
          }
        },
        { passive: false }
      );

      this.addEventListener(
        'touchmove',
        (event) => {
          if (!this.hasAttribute('open')) return;
          const content = event.target.closest('.product-media-modal__content') || this.querySelector('.product-media-modal__content');
          if (!content) return;

          if (this.zoomState.active && event.touches.length === 2) {
            event.preventDefault();
            this.onPinchMove(event);
            return;
          }

          if (this.zoomState.panning && event.touches.length === 1) {
            event.preventDefault();
            this.onPanMove(event);
            return;
          }

          if (this.touchSwipeState.active && event.touches.length === 1) {
            event.preventDefault();
            this.onTouchSwipeMove(event);
          }
        },
        { passive: false }
      );

      const onTouchEnd = (event) => {
        if (this.zoomState.active && event.touches.length < 2) this.endPinch();
        if (this.zoomState.panning && event.touches.length === 0) this.endPan();
        if (this.touchSwipeState.active && event.touches.length === 0) this.endTouchSwipe(event);
      };

      this.addEventListener('touchend', onTouchEnd, { passive: true });
      this.addEventListener('touchcancel', onTouchEnd, { passive: true });

      this.addEventListener(
        'pointermove',
        (event) => {
          if (!this.swipeState.active) return;
          if (event.pointerId !== this.swipeState.pointerId) return;

          const deltaX = event.clientX - this.swipeState.startX;
          const deltaY = event.clientY - this.swipeState.startY;

          if (this.swipeState.isHorizontal === null) {
            const absX = Math.abs(deltaX);
            const absY = Math.abs(deltaY);
            if (absX < 10 && absY < 10) return;
            this.swipeState.isHorizontal = absX > absY;
          }

          if (this.swipeState.isHorizontal) {
            event.preventDefault();
            const activeMedia = this.swipeState.activeMedia;
            const prevMedia = this.swipeState.prevMedia;
            const nextMedia = this.swipeState.nextMedia;
            const viewportWidth = this.swipeState.viewportWidth;

            if (!activeMedia || !prevMedia || !nextMedia) return;

            activeMedia.style.setProperty('--drag-x', `${deltaX}px`);

            if (deltaX < 0) {
              prevMedia.classList.remove('is-peek');
              prevMedia.style.removeProperty('--drag-x');
              nextMedia.classList.add('is-peek');
              nextMedia.style.setProperty('--drag-x', `${viewportWidth + deltaX}px`);
            } else {
              nextMedia.classList.remove('is-peek');
              nextMedia.style.removeProperty('--drag-x');
              prevMedia.classList.add('is-peek');
              prevMedia.style.setProperty('--drag-x', `${-viewportWidth + deltaX}px`);
            }
          } else {
            this.swipeState.active = false;
            this.swipeState.pointerId = null;
            this.swipeState.activeMedia = null;
            this.swipeState.prevMedia = null;
            this.swipeState.nextMedia = null;
            this.classList.remove('is-dragging');
          }
        },
        { passive: false }
      );

      const onPointerEnd = (event) => {
        if (!this.swipeState.active) return;
        if (event.pointerId !== this.swipeState.pointerId) return;

        const deltaX = event.clientX - this.swipeState.startX;
        const deltaY = event.clientY - this.swipeState.startY;

        const activeMedia = this.swipeState.activeMedia;
        const prevMedia = this.swipeState.prevMedia;
        const nextMedia = this.swipeState.nextMedia;
        const viewportWidth = this.swipeState.viewportWidth;

        this.swipeState.active = false;
        this.swipeState.pointerId = null;
        this.swipeState.activeMedia = null;
        this.swipeState.prevMedia = null;
        this.swipeState.nextMedia = null;

        if (!this.swipeState.isHorizontal) return;
        if (Math.abs(deltaY) > Math.abs(deltaX)) return;

        this.classList.remove('is-dragging');

        const threshold = 80;
        const shouldGoNext = deltaX <= -threshold;
        const shouldGoPrev = deltaX >= threshold;

        const clearPeek = () => {
          if (prevMedia) {
            prevMedia.classList.remove('is-peek');
            prevMedia.style.removeProperty('--drag-x');
          }
          if (nextMedia) {
            nextMedia.classList.remove('is-peek');
            nextMedia.style.removeProperty('--drag-x');
          }
        };

        if (!activeMedia || !prevMedia || !nextMedia) return;

        if (shouldGoNext) {
          activeMedia.style.setProperty('--drag-x', `${-viewportWidth}px`);
          nextMedia.classList.add('is-peek');
          nextMedia.style.setProperty('--drag-x', `0px`);

          window.setTimeout(() => {
            clearPeek();
            activeMedia.style.removeProperty('--drag-x');
            nextMedia.style.removeProperty('--drag-x');
            this.slide('next');
          }, 260);
          return;
        }

        if (shouldGoPrev) {
          activeMedia.style.setProperty('--drag-x', `${viewportWidth}px`);
          prevMedia.classList.add('is-peek');
          prevMedia.style.setProperty('--drag-x', `0px`);

          window.setTimeout(() => {
            clearPeek();
            activeMedia.style.removeProperty('--drag-x');
            prevMedia.style.removeProperty('--drag-x');
            this.slide('prev');
          }, 260);
          return;
        }

        activeMedia.style.setProperty('--drag-x', `0px`);
        window.setTimeout(() => {
          activeMedia.style.removeProperty('--drag-x');
          clearPeek();
        }, 260);
      };

      this.addEventListener('pointerup', onPointerEnd);
      this.addEventListener('pointercancel', onPointerEnd);
    }

    connectedCallback() {
      if (!this.moved) {
        this.moved = true;
        document.body.appendChild(this);
      }
    }

    clamp(number, min, max) {
      return Math.max(min, Math.min(number, max));
    }

    getTouchDistance(touch1, touch2) {
      const dx = touch2.clientX - touch1.clientX;
      const dy = touch2.clientY - touch1.clientY;
      return Math.hypot(dx, dy);
    }

    getTouchMidpoint(touch1, touch2) {
      return { x: (touch1.clientX + touch2.clientX) / 2, y: (touch1.clientY + touch2.clientY) / 2 };
    }

    resetTransforms() {
      this.classList.remove('is-dragging');
      this.classList.remove('is-zooming');
      this.querySelectorAll('.product-media-modal__content > .is-peek').forEach((element) => {
        element.classList.remove('is-peek');
      });
      this.querySelectorAll('.product-media-modal__content > [data-media-id]').forEach((element) => {
        element.style.removeProperty('--drag-x');
        element.style.removeProperty('--zoom-scale');
        element.style.removeProperty('--zoom-tx');
        element.style.removeProperty('--zoom-ty');
      });
      this.zoomState.active = false;
      this.zoomState.panning = false;
      this.zoomState.scale = 1;
      this.zoomState.tx = 0;
      this.zoomState.ty = 0;
      this.touchSwipeState.active = false;
    }

    applyZoom(media) {
      media.style.setProperty('--zoom-scale', `${this.zoomState.scale}`);
      media.style.setProperty('--zoom-tx', `${this.zoomState.tx}px`);
      media.style.setProperty('--zoom-ty', `${this.zoomState.ty}px`);
    }

    startPinch(event, content, media) {
      this.touchSwipeState.active = false;
      this.zoomState.active = true;
      this.zoomState.panning = false;
      const rect = content.getBoundingClientRect();
      this.zoomState.viewportWidth = rect.width || window.innerWidth;
      this.zoomState.viewportHeight = rect.height || window.innerHeight;

      const distance = this.getTouchDistance(event.touches[0], event.touches[1]);
      const midpoint = this.getTouchMidpoint(event.touches[0], event.touches[1]);

      this.zoomState.startDistance = distance;
      this.zoomState.startScale = this.zoomState.scale;
      this.zoomState.startTx = this.zoomState.tx;
      this.zoomState.startTy = this.zoomState.ty;
      this.zoomState.startMidX = midpoint.x;
      this.zoomState.startMidY = midpoint.y;

      this.classList.add('is-zooming');
      this.applyZoom(media);
    }

    onPinchMove(event) {
      const media = this.querySelector('.product-media-modal__content > .active');
      if (!media || media.tagName !== 'IMG') return;
      const distance = this.getTouchDistance(event.touches[0], event.touches[1]);
      const midpoint = this.getTouchMidpoint(event.touches[0], event.touches[1]);

      let scale = this.zoomState.startScale * (distance / this.zoomState.startDistance);
      scale = this.clamp(scale, 1, 3);

      let tx = this.zoomState.startTx + (midpoint.x - this.zoomState.startMidX);
      let ty = this.zoomState.startTy + (midpoint.y - this.zoomState.startMidY);

      const maxX = (this.zoomState.viewportWidth * (scale - 1)) / 2;
      const maxY = (this.zoomState.viewportHeight * (scale - 1)) / 2;
      tx = this.clamp(tx, -maxX, maxX);
      ty = this.clamp(ty, -maxY, maxY);

      this.zoomState.scale = scale;
      this.zoomState.tx = tx;
      this.zoomState.ty = ty;
      this.applyZoom(media);
    }

    endPinch() {
      const media = this.querySelector('.product-media-modal__content > .active');
      this.zoomState.active = false;
      this.classList.remove('is-zooming');
      if (!media || media.tagName !== 'IMG') return;

      if (this.zoomState.scale <= 1.01) {
        this.zoomState.scale = 1;
        this.zoomState.tx = 0;
        this.zoomState.ty = 0;
        media.style.removeProperty('--zoom-scale');
        media.style.removeProperty('--zoom-tx');
        media.style.removeProperty('--zoom-ty');
      } else {
        this.applyZoom(media);
      }
    }

    startPan(event, content, media) {
      this.touchSwipeState.active = false;
      this.zoomState.panning = true;
      this.zoomState.active = false;
      const rect = content.getBoundingClientRect();
      this.zoomState.viewportWidth = rect.width || window.innerWidth;
      this.zoomState.viewportHeight = rect.height || window.innerHeight;

      this.zoomState.lastX = event.touches[0].clientX;
      this.zoomState.lastY = event.touches[0].clientY;

      this.classList.add('is-zooming');
      this.applyZoom(media);
    }

    onPanMove(event) {
      const media = this.querySelector('.product-media-modal__content > .active');
      if (!media || media.tagName !== 'IMG') return;
      const currentX = event.touches[0].clientX;
      const currentY = event.touches[0].clientY;
      const dx = currentX - this.zoomState.lastX;
      const dy = currentY - this.zoomState.lastY;
      this.zoomState.lastX = currentX;
      this.zoomState.lastY = currentY;

      let tx = this.zoomState.tx + dx;
      let ty = this.zoomState.ty + dy;

      const scale = this.zoomState.scale;
      const maxX = (this.zoomState.viewportWidth * (scale - 1)) / 2;
      const maxY = (this.zoomState.viewportHeight * (scale - 1)) / 2;
      tx = this.clamp(tx, -maxX, maxX);
      ty = this.clamp(ty, -maxY, maxY);

      this.zoomState.tx = tx;
      this.zoomState.ty = ty;
      this.applyZoom(media);
    }

    endPan() {
      this.zoomState.panning = false;
      this.classList.remove('is-zooming');
    }

    startTouchSwipe(event, content, activeMedia) {
      if (this.zoomState.scale > 1) return;
      const mediaElements = Array.from(this.querySelectorAll('.product-media-modal__content > [data-media-id]'));
      if (mediaElements.length <= 1) return;

      const currentIndex = mediaElements.indexOf(activeMedia);
      const prevMedia = mediaElements[currentIndex - 1] || mediaElements[mediaElements.length - 1];
      const nextMedia = mediaElements[currentIndex + 1] || mediaElements[0];

      this.touchSwipeState.active = true;
      this.touchSwipeState.startX = event.touches[0].clientX;
      this.touchSwipeState.startY = event.touches[0].clientY;
      this.touchSwipeState.isHorizontal = null;
      this.touchSwipeState.activeMedia = activeMedia;
      this.touchSwipeState.prevMedia = prevMedia;
      this.touchSwipeState.nextMedia = nextMedia;
      this.touchSwipeState.viewportWidth = content.getBoundingClientRect().width || window.innerWidth;

      this.classList.add('is-dragging');
    }

    onTouchSwipeMove(event) {
      const deltaX = event.touches[0].clientX - this.touchSwipeState.startX;
      const deltaY = event.touches[0].clientY - this.touchSwipeState.startY;

      if (this.touchSwipeState.isHorizontal === null) {
        const absX = Math.abs(deltaX);
        const absY = Math.abs(deltaY);
        if (absX < 10 && absY < 10) return;
        this.touchSwipeState.isHorizontal = absX > absY;
      }

      if (!this.touchSwipeState.isHorizontal) {
        this.endTouchSwipe();
        return;
      }

      const activeMedia = this.touchSwipeState.activeMedia;
      const prevMedia = this.touchSwipeState.prevMedia;
      const nextMedia = this.touchSwipeState.nextMedia;
      const viewportWidth = this.touchSwipeState.viewportWidth;
      if (!activeMedia || !prevMedia || !nextMedia) return;

      activeMedia.style.setProperty('--drag-x', `${deltaX}px`);

      if (deltaX < 0) {
        prevMedia.classList.remove('is-peek');
        prevMedia.style.removeProperty('--drag-x');
        nextMedia.classList.add('is-peek');
        nextMedia.style.setProperty('--drag-x', `${viewportWidth + deltaX}px`);
      } else {
        nextMedia.classList.remove('is-peek');
        nextMedia.style.removeProperty('--drag-x');
        prevMedia.classList.add('is-peek');
        prevMedia.style.setProperty('--drag-x', `${-viewportWidth + deltaX}px`);
      }
    }

    endTouchSwipe() {
      if (!this.touchSwipeState.active) return;

      const activeMedia = this.touchSwipeState.activeMedia;
      const prevMedia = this.touchSwipeState.prevMedia;
      const nextMedia = this.touchSwipeState.nextMedia;
      const viewportWidth = this.touchSwipeState.viewportWidth;

      this.touchSwipeState.active = false;
      this.classList.remove('is-dragging');

      const clearPeek = () => {
        if (activeMedia) activeMedia.style.removeProperty('--drag-x');
        if (prevMedia) {
          prevMedia.classList.remove('is-peek');
          prevMedia.style.removeProperty('--drag-x');
        }
        if (nextMedia) {
          nextMedia.classList.remove('is-peek');
          nextMedia.style.removeProperty('--drag-x');
        }
      };

      if (!this.touchSwipeState.isHorizontal) {
        clearPeek();
        return;
      }
      if (!activeMedia || !prevMedia || !nextMedia) {
        clearPeek();
        return;
      }

      const currentDragX = parseFloat(activeMedia.style.getPropertyValue('--drag-x')) || 0;
      const threshold = 80;
      const shouldGoNext = currentDragX <= -threshold;
      const shouldGoPrev = currentDragX >= threshold;

      if (shouldGoNext) {
        activeMedia.style.setProperty('--drag-x', `${-viewportWidth}px`);
        nextMedia.classList.add('is-peek');
        nextMedia.style.setProperty('--drag-x', `0px`);
        window.setTimeout(() => {
          clearPeek();
          this.slide('next');
        }, 260);
        return;
      }

      if (shouldGoPrev) {
        activeMedia.style.setProperty('--drag-x', `${viewportWidth}px`);
        prevMedia.classList.add('is-peek');
        prevMedia.style.setProperty('--drag-x', `0px`);
        window.setTimeout(() => {
          clearPeek();
          this.slide('prev');
        }, 260);
        return;
      }

      activeMedia.style.setProperty('--drag-x', `0px`);
      window.setTimeout(() => {
        clearPeek();
      }, 260);
    }

    hide() {
      document.body.classList.remove('overflow-hidden');
      this.removeAttribute('open');
      if (this.openedBy) this.openedBy.focus();
      if (typeof window.pauseAllMedia === 'function') window.pauseAllMedia();
      this.resetTransforms();
    }

    show(opener) {
      this.openedBy = opener;
      document.body.classList.add('overflow-hidden');
      this.setAttribute('open', '');
      this.showActiveMedia();
    }

    showActiveMedia() {
      this.querySelectorAll('.product-media-modal__content > .active').forEach((element) => {
          element.classList.remove('active');
        }
      )
      this.resetTransforms();
      const activeMedia = this.querySelector(`[data-media-id="${this.openedBy.getAttribute("data-media-id")}"]`);
      if (!activeMedia) return;
      activeMedia.classList.add('active');

      const activeMediaTemplate = activeMedia.querySelector('template');
      const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;

      if (activeMedia.nodeName == 'DEFERRED-MEDIA' && activeMediaContent && activeMediaContent.querySelector('.js-youtube'))
        activeMedia.loadContent();
    }

    slide(direction) {
      this.resetTransforms();
      const activeMedia = this.querySelector('.product-media-modal__content > .active');
      if (!activeMedia) return;

      const mediaElements = Array.from(this.querySelectorAll('.product-media-modal__content > [data-media-id]'));
      if (mediaElements.length <= 1) return;

      const currentIndex = mediaElements.indexOf(activeMedia);
      let nextMedia;

      if (direction === 'next') {
        nextMedia = mediaElements[currentIndex + 1] || mediaElements[0];
      } else {
        nextMedia = mediaElements[currentIndex - 1] || mediaElements[mediaElements.length - 1];
      }

      if (nextMedia) {
        if (typeof window.pauseAllMedia === 'function') window.pauseAllMedia();
        
        activeMedia.classList.remove('active');
        nextMedia.classList.add('active');
        
        // Update openedBy to keep track of current media
        if (this.openedBy) this.openedBy.setAttribute('data-media-id', nextMedia.getAttribute('data-media-id'));
        
        const nextMediaTemplate = nextMedia.querySelector('template');
        const nextMediaContent = nextMediaTemplate ? nextMediaTemplate.content : null;
        if (nextMedia.nodeName == 'DEFERRED-MEDIA' && nextMediaContent && nextMediaContent.querySelector('.js-youtube'))
          nextMedia.loadContent();
      }
    }
  });
}
